/**
 * Support access break-glass API client.
 *
 * Backed by services/api/support-access-* Lambdas. The /admin vs /org route
 * choice is made by the caller — the platform pages hit /admin, tenant pages
 * hit /org. Backend re-enforces role + tenant boundary on each route.
 */

import { api } from './client';

export type SupportRequestStatus =
  | 'pending'
  | 'active'
  | 'rejected'
  | 'revoked'
  | 'expired'
  | 'ended_by_user';

export type DecisionPath = 'customer' | 'break_glass';

export interface SupportAccessRequest {
  request_id: string;
  org_id: string;
  actor_user_id: string;
  actor_email?: string;
  status: SupportRequestStatus;
  justification?: string;
  duration_minutes?: number;
  requested_at?: string;
  decided_at?: string;
  decided_by?: string;
  decided_by_email?: string;
  decision_path?: DecisionPath;
  break_glass_reason?: string;
  expires_at?: string;
  ended_at?: string;
  end_reason?: string;
}

export interface ListResponse {
  items: SupportAccessRequest[];
  count: number;
}

export async function listAdmin(statusFilter?: SupportRequestStatus): Promise<ListResponse> {
  const qs = statusFilter ? `?status=${encodeURIComponent(statusFilter)}` : '';
  return api<ListResponse>(`/admin/support-access/requests${qs}`);
}

export async function listOrg(statusFilter?: SupportRequestStatus): Promise<ListResponse> {
  const qs = statusFilter ? `?status=${encodeURIComponent(statusFilter)}` : '';
  return api<ListResponse>(`/org/support-access/requests${qs}`);
}

export interface CreateRequestBody {
  org_id: string;
  justification: string;
  duration_minutes: number;
}

export async function createRequest(body: CreateRequestBody): Promise<SupportAccessRequest> {
  return api<SupportAccessRequest>('/admin/support-access/requests', {
    method: 'POST',
    body,
  });
}

export async function approve(requestId: string): Promise<{ request_id: string; status: SupportRequestStatus }> {
  return api(`/org/support-access/requests/${encodeURIComponent(requestId)}/approve`, {
    method: 'POST',
    body: {},
  });
}

export async function revoke(requestId: string, reason?: string): Promise<{ request_id: string; status: SupportRequestStatus }> {
  return api(`/org/support-access/requests/${encodeURIComponent(requestId)}/revoke`, {
    method: 'POST',
    body: { reason },
  });
}

export async function breakGlass(
  requestId: string,
  break_glass_reason: string
): Promise<{ request_id: string; status: SupportRequestStatus }> {
  return api(`/admin/support-access/requests/${encodeURIComponent(requestId)}/break-glass`, {
    method: 'POST',
    body: { break_glass_reason },
  });
}

export async function endGrant(requestId: string, reason?: string): Promise<{ request_id: string; status: SupportRequestStatus }> {
  return api(`/admin/support-access/requests/${encodeURIComponent(requestId)}/end`, {
    method: 'POST',
    body: { reason },
  });
}

export function isActive(r: SupportAccessRequest): boolean {
  return r.status === 'active';
}

export function timeRemaining(r: SupportAccessRequest): number {
  if (r.status !== 'active' || !r.expires_at) return 0;
  return Math.max(0, new Date(r.expires_at).getTime() - Date.now());
}
